import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatAutocomplete } from '@angular/material';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { TranslateService } from '@ngx-translate/core';
import { chain, cloneDeep, findIndex, some } from 'lodash';
import { Subject } from 'rxjs';
import { debounceTime, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import english from './i18n/en.json';
import spanish from './i18n/es.json';
import portuguese from './i18n/pt.json';

export enum searchManyInputStyles {
  Select = 'Select',
  Outlined = 'Outlined',
  Underline = 'Underline'
}

@Component({
  selector: 'fc-search-many-input',
  templateUrl: './fc-search-many-input.component.html',
  styleUrls: ['./fc-search-many-input.component.scss']
})
export class FcSearchManyInputComponent implements OnInit, OnDestroy {
  @Input()
  public control: FormControl;

  @Input()
  public requiredMark = false;

  @Input()
  public label: string;

  @Input()
  public desiredStyle: searchManyInputStyles = searchManyInputStyles.Select;

  @Input()
  public disabled = false;

  @Input()
  public showSearchIcon = false;

  @Input()
  public tooltipPosition: 'above' | 'below' | 'left' | 'right' = 'below';

  @Input()
  public search: (searchTerm: string, limit: number, offset: number) => Promise<any[]>;

  @Input()
  public displayWith: (value: any) => string;

  @ViewChild('displayTextInput', { static: true })
  private displayTextInput: ElementRef<HTMLInputElement>;

  @ViewChild('inputFilter', { static: true })
  private inputFilter: ElementRef<HTMLInputElement>;

  @ViewChild('matAutocomplete', { static: true })

  private matAutocomplete: MatAutocomplete;
  private readonly optionsLimit = 10;
  private readonly removeSubscriptions = new Subject<void>();
  private scrollHandler: Function;

  public options = [];
  public loading = false;
  public displayText = '';
  public hasMoreOptions = true;
  public optionsOffset = 0;
  public styleToDisplay = 'select-style';

  public readonly searchTerm = new FormControl(null);

  constructor(
    private fuseTranslationLoaderService: FuseTranslationLoaderService,
    private translate: TranslateService
  ) {
    this.fuseTranslationLoaderService.loadJsonTranslations({ english, portuguese, spanish });
  }

  ngOnDestroy(): void {
    this.removeSubscriptions.next();
    this.removeSubscriptions.complete();
  }

  ngOnInit(): void {
    const controlValue = this.control.value || [];

    this.styleToDisplay = this.getDesiredStyles();

    this.displayText = this.formatDisplayText(controlValue);
    this.options = cloneDeep(controlValue);

    this.searchTerm.valueChanges
      .pipe(
        takeUntil(this.removeSubscriptions),
        debounceTime(500),
        tap(() => this.resetValues()),
        map((value) => value || ''),
        filter((value) => typeof value === 'string'),
        map((value) => value.trim()),
        tap(() => this.loading = true),
        switchMap(() => this.search(this.inputFilter.nativeElement.value, this.optionsLimit, 0)),
        tap(() => this.loading = false)
      )
      .subscribe((options) => {
        if (options.length < this.optionsLimit) {
          this.hasMoreOptions = false;
        }
        this.keepOptionChecked(options);
        this.options = options;
      });

    this.control.valueChanges.pipe(takeUntil(this.removeSubscriptions)).subscribe((value) => {
      value = value || [];
      this.displayText = this.formatDisplayText(value);

      if (!value.length) {
        this.options.forEach((option) => {
          if (option.selected) {
            option.selected = false;
          }
        });
        this.resetValues();
      }
    });

    this.searchTerm.reset();
  }

  private formatDisplayText(controlValue): string {
    const archivedLabel = this.translate.instant('SEARCH_MANY_INPUT.ARCHIVED_OPTION').toLowerCase();
    const formattedDisplayText = controlValue.map((value) => {
      if (value.archived) {
        return `${value.name} (${archivedLabel})`;
      } else {
        return value.name;
      }
    }).join(', ');

    return formattedDisplayText;
  }

  private getDesiredStyles(): string {
    switch (this.desiredStyle) {
      case searchManyInputStyles.Select:
        return 'select-style';
      case searchManyInputStyles.Outlined:
        return 'outlined-style';
      case searchManyInputStyles.Underline:
        return 'underline-style';
      default:
        return 'select-style';
    }
  }

  resetValues(): void {
    this.optionsOffset = 0;
    this.hasMoreOptions = true;
  }

  keepOptionChecked(options): void {
    const currentValue = this.control.value || [];
    options.forEach((option) => {
      option.selected = some(currentValue, {
        id: option.id
      });
    });
  }

  loadMoreOptions(): void {
    this.search(this.inputFilter.nativeElement.value, this.optionsLimit, this.optionsOffset).then(((options) => {
      this.keepOptionChecked(options);
      this.options.push(...options);
      this.loading = false;
      if (options.length < this.optionsLimit) {
        this.hasMoreOptions = false;
      }
    }));
  }

  onAutocompleteScroll(): void {
    if (this.options.length) {
      this.loading = false;
    }

    setTimeout(() => {
      if (
        this.matAutocomplete &&
        this.matAutocomplete.panel &&
        this.matAutocomplete.panel.nativeElement
      ) {
        this.matAutocomplete.panel.nativeElement.scrollTo({
          top: 0,
        });

        this.scrollHandler = (event: any): void => {
          if (this.hasMoreOptions) {
            const {
              scrollTop,
              scrollHeight,
              clientHeight
            } = event.target;

            const marginOfError = 1;

            if ((scrollTop + clientHeight) >= (scrollHeight - marginOfError)) {
              this.loading = true;
              this.optionsOffset += this.optionsLimit;
              this.loadMoreOptions();
            }
          }
        };

        this.matAutocomplete.panel.nativeElement.addEventListener(
          'scroll',
          this.scrollHandler
        );
      }
    });
  }


  onOptionCleared(): void {
    this.control.setValue(null);
    this.displayTextInput.nativeElement.focus();
  }

  onBlur(): void {
    if (
      this.hasMoreOptions &&
      !this.options.length
    ) {
      this.searchTerm.setValue('');
    }
  }

  onCheck(event: PointerEvent, option: any): void {
    event.stopPropagation();
    event.preventDefault();

    this.displayTextInput.nativeElement.click();

    const value = this.control.value || [];
    const index = findIndex(value, {
      id: option.id
    });

    const selected = index >= 0;
    if (selected) {
      value.splice(index, 1);
    } else {
      value.push(option);
    }
    option.selected = !selected;

    const newValue = chain(value).sortBy('name').cloneDeep().value();
    this.control.setValue(newValue.length ? newValue : null);
  }

  onCloseAutocomplete(): void {
    this.inputFilter.nativeElement.value = '';
  }

  onClickInputFilter(event: PointerEvent): void {
    event.stopPropagation();
    this.inputFilter.nativeElement.focus();
  }

  openPanel(event: Event): void {
    event.stopPropagation();
    this.displayTextInput.nativeElement.focus();
    this.displayTextInput.nativeElement.click();
  }

  isSelected(option: any): boolean {
    const value = this.control.value || [];

    return some(value, {
      id: option.id
    });
  }

  clearSelection(): void {
    this.control.setValue(null);
    this.searchTerm.setValue('');
    this.options.forEach((option) => {
      option.selected = false;
    });
  }
}
